import { ArrowsOutSimple } from '@liveauctioneers/hammer-ui-core/icons';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { getConsoleImageCarouselUrl, getImageUrl } from '@/utils/urls';
import {
    getItemSummaryCatalogId,
    getItemSummaryImageVersion,
    getItemSummaryPhotos,
    getItemSummarySellerId,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLocale } from '@/redux/modules/intl';
import {
    ImageSlider,
    ImageSliderAnalyticsEvent,
    ImageSliderCard,
    ThumbnailDisplay,
} from '@liveauctioneers/hammer-ui-core/imageSlider';
import { popupCenter } from '@/utils/popupCenter';
import { transparentNeutralClasses } from '@liveauctioneers/hammer-ui-core/iconButton';
import { useAppSelector } from '@/redux/hooks';
import { useCallback, useMemo, useState } from 'react';
import { useTrackItemImageGalleryInteractionAnalytics } from '@/utils/amplitude/analytics/item/trackItemImageGalleryInteraction.analytics';

const IMAGE_CAROUSEL_POPUP_HEIGHT = 1000;
const IMAGE_CAROUSEL_POPUP_WIDTH = 1000;

type Props = {
    className?: string;
    itemId: number;
};

export const BiddingConsoleImageSlider = ({ className, itemId }: Props) => {
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const imageVersion = useAppSelector((state) => getItemSummaryImageVersion(state, itemId));
    const photos = useAppSelector((state) => getItemSummaryPhotos(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const locale = useAppSelector(getLocale);

    const localePath = locale === 'en-gb' ? `/${locale}` : '';
    const trackImageGalleryInteraction = useTrackItemImageGalleryInteractionAnalytics();
    const [imageGalleryIndex, setImageGalleryIndex] = useState(0);

    const handleImageGalleryAnalytics = useCallback(
        (event: ImageSliderAnalyticsEvent) => {
            setImageGalleryIndex(event.imagePosition - 1);
            trackImageGalleryInteraction(event);
        },
        [trackImageGalleryInteraction]
    );

    const openImageCarouselPopout = useCallback(
        (indexOverride?: number) => {
            const index = indexOverride ?? imageGalleryIndex;
            const imageNum = index + 1;
            trackImageGalleryInteraction({
                imagePosition: imageNum,
                interactionType: 'Open Lightbox',
                totalImages: photos.length,
            });
            popupCenter({
                height: IMAGE_CAROUSEL_POPUP_HEIGHT,
                title: `${title} Image`,
                url: localePath + getConsoleImageCarouselUrl(catalogId, itemId, imageNum),
                width: IMAGE_CAROUSEL_POPUP_WIDTH,
            });
        },
        [catalogId, itemId, localePath, photos.length, title, trackImageGalleryInteraction, imageGalleryIndex]
    );

    const images = useMemo(() => {
        const imageGalleryImages: ImageSliderCard[] = [];

        photos.forEach((photo, index) => {
            const alt = index ? `${title} - ${index + 1}` : title;
            const src = getImageUrl({
                catalogId,
                imageNumber: photo,
                imageVersion,
                itemId,
                quality: 80,
                sellerId,
                sharpen: true,
            });

            imageGalleryImages.push({
                imageData: {
                    alt: alt,
                    baseUrl: src,
                    title,
                },
                key: `responsive-image-${index + 1}`,
            });
        });

        return imageGalleryImages;
    }, [photos, title, catalogId, imageVersion, itemId, sellerId]);

    return (
        <div className={cn('relative', className)}>
            <div className="flex justify-center">
                <ImageSlider
                    cards={images}
                    contentKey={itemId}
                    iconShape="square"
                    iconSize="sm"
                    iconType="arrow"
                    iconVariant="outlined"
                    onAnalyticsEventTriggered={handleImageGalleryAnalytics}
                    responsiveImageConfig={{
                        breakpoint: 768,
                        desktopSize: 651,
                        mobileSize: 366,
                    }}
                    showThumbnails
                    thumbnailDisplay={ThumbnailDisplay.Index}
                    thumbnailOrientation="horizontal"
                />
            </div>
            {/* Native button used instead of hammer-ui IconButton because the latter has a built-in popover that conflicts with our open-in-new-window behavior. */}
            <button
                aria-label="Open image in new window"
                className={cn(
                    'hui-focus-visible-outline flex items-center justify-center rounded-full disabled:text-text-disabled',
                    'absolute bottom-xs right-0 z-20',
                    'h-lg min-h-lg w-lg min-w-lg',
                    transparentNeutralClasses
                )}
                onClick={() => {
                    openImageCarouselPopout();
                    handleImageGalleryAnalytics({
                        imagePosition: imageGalleryIndex + 1,
                        interactionType: 'Open Lightbox',
                        totalImages: photos.length,
                    });
                }}
                type="button"
            >
                <ArrowsOutSimple
                    $withoutAriaLabel
                    className="rotate-[10deg]"
                    color="inherit"
                    size="sm"
                />
            </button>
        </div>
    );
};
